/**
 * Custom Reviews Ecosystem — JavaScript
 * Modules: Tab Filtering, Photo Modal, Carousel, Review Form, Social Proof
 * Each module self-initializes based on DOM presence.
 */
(function () {
  'use strict';

  /* ===================================================================
     TAB FILTERING
     Handles show/hide of review cards based on review type tabs.
     =================================================================== */
  /* Apply the currently active tab filter to a set of cards */
  function applyActiveFilter(section, cards) {
    var activeTab = section.querySelector('.reviews-tab.is-active');
    var filter = activeTab ? activeTab.getAttribute('data-tab') : 'all';
    cards.forEach(function (card) {
      if (filter === 'all' || card.getAttribute('data-review-type') === filter) {
        card.removeAttribute('data-hidden');
        card.style.display = '';
      } else {
        card.setAttribute('data-hidden', 'true');
        card.style.display = 'none';
      }
    });
  }

  function initTabs() {
    var tablists = document.querySelectorAll('.reviews-tabs');
    if (!tablists.length) return;

    tablists.forEach(function (tablist) {
      var section = tablist.closest('.reviews-section') || tablist.parentElement;
      var tabs = tablist.querySelectorAll('.reviews-tab');
      var grid = section.querySelector('.reviews-grid');
      if (!grid) return;

      tabs.forEach(function (tab) {
        tab.addEventListener('click', function () {
          tabs.forEach(function (t) {
            t.classList.remove('is-active');
            t.setAttribute('aria-selected', 'false');
          });
          tab.classList.add('is-active');
          tab.setAttribute('aria-selected', 'true');

          applyActiveFilter(section, grid.querySelectorAll('.review-card'));
        });
      });
    });
  }

  /* ===================================================================
     LOAD MORE (AJAX via Shopify Section Rendering API)
     Fetches the next paginated page and appends the review cards.
     =================================================================== */
  function initLoadMore() {
    var buttons = document.querySelectorAll('[data-load-more]');
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      var section = btn.closest('.reviews-section');
      if (!section) return;
      var grid = section.querySelector('.reviews-grid');
      if (!grid) return;

      var originalText = btn.textContent.trim();

      btn.addEventListener('click', function () {
        var sectionId = btn.getAttribute('data-section-id');
        var current = parseInt(btn.getAttribute('data-current-page'), 10) || 1;
        var total = parseInt(btn.getAttribute('data-total-pages'), 10) || 1;
        var next = current + 1;
        if (next > total) {
          btn.hidden = true;
          return;
        }

        btn.disabled = true;
        btn.textContent = 'Loading…';

        var url = window.location.pathname +
          '?section_id=' + encodeURIComponent(sectionId) +
          '&page=' + next;

        fetch(url, { credentials: 'same-origin' })
          .then(function (res) {
            if (!res.ok) throw new Error('HTTP ' + res.status);
            return res.text();
          })
          .then(function (html) {
            var parser = new DOMParser();
            var doc = parser.parseFromString(html, 'text/html');
            var newCards = Array.prototype.slice.call(doc.querySelectorAll('.review-card'));

            newCards.forEach(function (card) {
              grid.appendChild(card);
            });

            applyActiveFilter(section, newCards);

            btn.setAttribute('data-current-page', String(next));
            btn.disabled = false;
            btn.textContent = originalText;
            if (next >= total) btn.hidden = true;
          })
          .catch(function () {
            btn.disabled = false;
            btn.textContent = originalText;
          });
      });
    });
  }

  /* ===================================================================
     READ MORE
     Expands truncated review text.
     =================================================================== */
  function initReadMore() {
    document.addEventListener('click', function (e) {
      if (!e.target.classList.contains('review-card__read-more')) return;

      var btn = e.target;
      var textEl = btn.previousElementSibling;
      if (!textEl) return;

      var fullText = textEl.getAttribute('data-full-text');
      if (fullText) {
        textEl.textContent = fullText;
        textEl.removeAttribute('data-full-text');
        btn.remove();
      }
    });
  }

  /* ===================================================================
     PHOTO MODAL
     Opens a lightbox when a review photo is clicked.
     =================================================================== */
  function initPhotoModal() {
    var modal = document.getElementById('review-photo-modal');
    if (!modal) return;

    var modalImage = modal.querySelector('.review-modal__image');
    var nameEl = modal.querySelector('.review-modal__name');
    var verifiedEl = modal.querySelector('.review-modal__verified');
    var dateEl = modal.querySelector('.review-modal__date');
    var starsEl = modal.querySelector('.review-modal__stars');
    var bodyEl = modal.querySelector('.review-modal__body');
    var productEl = modal.querySelector('.review-modal__product');
    var closeButtons = modal.querySelectorAll('[data-modal-close]');

    function renderStars(rating) {
      var r = parseInt(rating, 10) || 0;
      if (r < 0) r = 0;
      if (r > 5) r = 5;
      var filled = '★'.repeat(r);
      var empty = '☆'.repeat(5 - r);
      return filled + empty;
    }

    function openModal(data) {
      modalImage.src = data.src || '';
      modalImage.alt = data.name ? 'Review photo by ' + data.name : 'Review photo';
      if (nameEl) nameEl.textContent = data.name || '';
      if (dateEl) dateEl.textContent = data.date || '';
      if (starsEl) starsEl.textContent = data.rating ? renderStars(data.rating) : '';
      if (bodyEl) bodyEl.textContent = data.body || '';
      if (verifiedEl) {
        verifiedEl.hidden = !(data.verified === 'true' || data.verified === true);
      }
      if (productEl) {
        if (data.product) {
          productEl.textContent = data.product;
          productEl.hidden = false;
        } else {
          productEl.textContent = '';
          productEl.hidden = true;
        }
      }
      modal.removeAttribute('hidden');
      document.body.style.overflow = 'hidden';
      closeButtons[closeButtons.length - 1].focus();
    }

    function closeModal() {
      modal.setAttribute('hidden', '');
      modalImage.src = '';
      document.body.style.overflow = '';
    }

    function dataFromPhoto(photo) {
      return {
        src: photo.getAttribute('data-full-src') || photo.src,
        name: photo.getAttribute('data-review-name') || '',
        date: photo.getAttribute('data-review-date') || '',
        rating: photo.getAttribute('data-review-rating') || '',
        body: photo.getAttribute('data-review-body') || '',
        verified: photo.getAttribute('data-review-verified') || '',
        product: photo.getAttribute('data-review-product') || ''
      };
    }

    // Click on review photos
    document.addEventListener('click', function (e) {
      var photo = e.target.closest('.review-card__photo');
      if (!photo) return;
      openModal(dataFromPhoto(photo));
    });

    // Keyboard on review photos
    document.addEventListener('keydown', function (e) {
      if (e.key === 'Enter' || e.key === ' ') {
        var photo = e.target.closest('.review-card__photo');
        if (photo) {
          e.preventDefault();
          openModal(dataFromPhoto(photo));
        }
      }
    });

    // Close handlers
    closeButtons.forEach(function (btn) {
      btn.addEventListener('click', closeModal);
    });

    // Click anywhere outside the card container closes the modal
    modal.addEventListener('click', function (e) {
      if (!e.target.closest('.review-modal__container')) {
        closeModal();
      }
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape' && !modal.hasAttribute('hidden')) {
        closeModal();
      }
    });

    // Custom event support
    window.addEventListener('review:photo:open', function (e) {
      if (e.detail && e.detail.src) {
        openModal(e.detail);
      }
    });
  }

  /* ===================================================================
     CAROUSEL
     Scroll-snap carousel with arrow navigation and auto-play.
     =================================================================== */
  function initCarousels() {
    var carousels = document.querySelectorAll('.review-carousel');
    if (!carousels.length) return;

    carousels.forEach(function (carousel) {
      var track = carousel.querySelector('.review-carousel__track');
      var prevBtn = carousel.querySelector('.review-carousel__arrow--prev');
      var nextBtn = carousel.querySelector('.review-carousel__arrow--next');
      if (!track) return;

      var autoPlay = carousel.getAttribute('data-auto-play') === 'true';
      var autoPlaySpeed = parseInt(carousel.getAttribute('data-auto-play-speed'), 10) || 5000;
      var autoPlayTimer = null;

      function getSlideWidth() {
        var slide = track.querySelector('.review-carousel__slide');
        if (!slide) return 300;
        return slide.offsetWidth + parseInt(getComputedStyle(track).gap, 10) || 20;
      }

      function scrollPrev() {
        track.scrollBy({ left: -getSlideWidth(), behavior: 'smooth' });
      }

      function scrollNext() {
        track.scrollBy({ left: getSlideWidth(), behavior: 'smooth' });
      }

      function updateArrows() {
        if (!prevBtn || !nextBtn) return;
        var scrollLeft = Math.round(track.scrollLeft);
        var maxScroll = track.scrollWidth - track.clientWidth;
        prevBtn.disabled = scrollLeft <= 1;
        nextBtn.disabled = scrollLeft >= maxScroll - 1;
      }

      if (prevBtn) prevBtn.addEventListener('click', scrollPrev);
      if (nextBtn) nextBtn.addEventListener('click', scrollNext);
      track.addEventListener('scroll', updateArrows, { passive: true });
      updateArrows();

      // Auto-play
      if (autoPlay) {
        function startAutoPlay() {
          stopAutoPlay();
          autoPlayTimer = setInterval(function () {
            var maxScroll = track.scrollWidth - track.clientWidth;
            if (Math.round(track.scrollLeft) >= maxScroll - 1) {
              track.scrollTo({ left: 0, behavior: 'smooth' });
            } else {
              scrollNext();
            }
          }, autoPlaySpeed);
        }

        function stopAutoPlay() {
          if (autoPlayTimer) {
            clearInterval(autoPlayTimer);
            autoPlayTimer = null;
          }
        }

        carousel.addEventListener('mouseenter', stopAutoPlay);
        carousel.addEventListener('mouseleave', startAutoPlay);
        carousel.addEventListener('focusin', stopAutoPlay);
        carousel.addEventListener('focusout', startAutoPlay);
        track.addEventListener('touchstart', stopAutoPlay, { passive: true });
        track.addEventListener('touchend', function () {
          setTimeout(startAutoPlay, 3000);
        }, { passive: true });

        startAutoPlay();
      }
    });
  }

  /* ===================================================================
     REVIEW FORM
     Handles interactive star rating, photo preview, validation,
     and form submission.
     =================================================================== */
  function initReviewForm() {
    var forms = document.querySelectorAll('.review-form__body');
    if (!forms.length) return;

    forms.forEach(function (form) {
      var formWrap = form.closest('.review-form');

      // Interactive star rating
      var starGroup = form.querySelector('.star-rating--interactive');
      if (starGroup) {
        var starInputs = starGroup.querySelectorAll('.star-rating__input');
        starInputs.forEach(function (input) {
          input.addEventListener('change', function () {
            starGroup.setAttribute('data-selected', input.value);
          });
        });

        // Hover effect
        var labels = starGroup.querySelectorAll('.star-rating__input-label');
        labels.forEach(function (label, idx) {
          label.addEventListener('mouseenter', function () {
            labels.forEach(function (l, i) {
              var star = l.querySelector('.star-rating__star');
              if (star) {
                star.style.fill = i <= idx
                  ? 'var(--color-review-star)'
                  : 'var(--color-review-star-empty)';
              }
            });
          });
        });

        starGroup.addEventListener('mouseleave', function () {
          var selected = starGroup.getAttribute('data-selected');
          labels.forEach(function (l, i) {
            var star = l.querySelector('.star-rating__star');
            if (star) {
              star.style.fill = selected && i < parseInt(selected, 10)
                ? 'var(--color-review-star)'
                : 'var(--color-review-star-empty)';
            }
          });
        });
      }

      // Photo preview
      var fileInput = form.querySelector('.review-form__file-input');
      var preview = form.querySelector('.review-form__photo-preview');
      if (fileInput && preview) {
        fileInput.addEventListener('change', function () {
          var file = fileInput.files[0];
          if (!file) {
            preview.classList.remove('is-visible');
            return;
          }
          var reader = new FileReader();
          reader.onload = function (e) {
            preview.innerHTML = '<img src="' + e.target.result + '" alt="Preview">';
            preview.classList.add('is-visible');
          };
          reader.readAsDataURL(file);
        });
      }

      // Review type toggle (show/hide product selector)
      var typeRadios = form.querySelectorAll('input[name="contact[review_type]"]');
      var productField = form.querySelector('[data-field="product"]');
      if (typeRadios.length && productField) {
        typeRadios.forEach(function (radio) {
          radio.addEventListener('change', function () {
            if (radio.value === 'product_review') {
              productField.style.display = '';
            } else {
              productField.style.display = 'none';
            }
          });
        });
      }

      // Form validation and submission
      form.addEventListener('submit', function (e) {
        var honeypot = form.querySelector('.review-form__honeypot input');
        if (honeypot && honeypot.value) {
          e.preventDefault();
          return;
        }

        // Basic client-side validation
        var errors = false;
        var requiredFields = form.querySelectorAll('[required]');
        requiredFields.forEach(function (field) {
          var errorEl = field.closest('.review-form__field');
          var errorMsg = errorEl ? errorEl.querySelector('.review-form__field-error') : null;

          if (!field.value || !field.value.trim()) {
            errors = true;
            field.style.borderColor = '#d32f2f';
            if (errorMsg) errorMsg.classList.add('is-visible');
          } else {
            field.style.borderColor = '';
            if (errorMsg) errorMsg.classList.remove('is-visible');
          }
        });

        // Check star rating
        if (starGroup) {
          var selectedStar = form.querySelector('.star-rating__input:checked');
          if (!selectedStar) {
            errors = true;
            starGroup.style.outline = '2px solid #d32f2f';
            starGroup.style.borderRadius = '4px';
          } else {
            starGroup.style.outline = '';
          }
        }

        if (errors) {
          e.preventDefault();
          return;
        }

        // Show success message (Shopify will handle the actual submission)
        // For AJAX submission, prevent default and handle manually
        var successEl = formWrap ? formWrap.querySelector('.review-form__success') : null;
        if (successEl && form.getAttribute('data-ajax') === 'true') {
          e.preventDefault();
          var submitBtn = form.querySelector('.review-form__submit');
          if (submitBtn) {
            submitBtn.disabled = true;
            submitBtn.textContent = 'Submitting...';
          }

          var formData = new FormData(form);
          fetch(form.action, {
            method: 'POST',
            body: formData,
            headers: { 'Accept': 'application/json' }
          })
          .then(function () {
            form.style.display = 'none';
            successEl.classList.add('is-visible');
          })
          .catch(function () {
            var errorEl = formWrap ? formWrap.querySelector('.review-form__error') : null;
            if (errorEl) errorEl.classList.add('is-visible');
            if (submitBtn) {
              submitBtn.disabled = false;
              submitBtn.textContent = 'Submit Review';
            }
          });
        }
      });
    });
  }

  /* ===================================================================
     SOCIAL PROOF POPUP
     Cycles through recent reviews as toast notifications.
     =================================================================== */
  function initSocialProof() {
    var containers = document.querySelectorAll('.social-proof');
    if (!containers.length) return;

    containers.forEach(function (container) {
      var dataEl = container.querySelector('script[type="application/json"]');
      if (!dataEl) return;

      var reviews;
      try {
        reviews = JSON.parse(dataEl.textContent);
      } catch (e) {
        return;
      }

      if (!reviews || !reviews.length) return;

      var popup = container.querySelector('.social-proof__popup');
      var closeBtn = container.querySelector('.social-proof__close');
      if (!popup) return;

      var displayDuration = parseInt(container.getAttribute('data-display-duration'), 10) || 5000;
      var interval = parseInt(container.getAttribute('data-interval'), 10) || 20000;
      var maxPopups = parseInt(container.getAttribute('data-max-popups'), 10) || 5;
      var sessionKey = 'review_social_proof_count';
      var dismissKey = 'review_social_proof_dismissed';

      // Check session state
      if (sessionStorage.getItem(dismissKey) === 'true') return;

      var shownCount = parseInt(sessionStorage.getItem(sessionKey), 10) || 0;
      if (shownCount >= maxPopups) return;

      var currentIndex = Math.floor(Math.random() * reviews.length);
      var showTimer = null;
      var hideTimer = null;

      function showPopup() {
        if (shownCount >= maxPopups) return;

        var review = reviews[currentIndex % reviews.length];
        currentIndex++;

        // Populate popup
        var nameEl = popup.querySelector('.social-proof__name');
        var msgEl = popup.querySelector('.social-proof__message');
        var photoEl = popup.querySelector('.social-proof__photo');
        var placeholderEl = popup.querySelector('.social-proof__photo-placeholder');
        var starsEl = popup.querySelector('.social-proof__stars');

        if (nameEl) nameEl.textContent = review.name || 'A Customer';
        if (msgEl) {
          var msg = 'left a ' + (review.rating || 5) + '-star review';
          if (review.product) msg += ' on ' + review.product;
          msgEl.textContent = msg;
        }

        if (photoEl && review.photo) {
          photoEl.src = review.photo;
          photoEl.style.display = '';
          if (placeholderEl) placeholderEl.style.display = 'none';
        } else if (placeholderEl) {
          var initial = (review.name || 'C').charAt(0).toUpperCase();
          placeholderEl.textContent = initial;
          placeholderEl.style.display = '';
          if (photoEl) photoEl.style.display = 'none';
        }

        // Show
        popup.classList.add('is-visible');
        shownCount++;
        sessionStorage.setItem(sessionKey, shownCount.toString());

        // Auto-hide
        hideTimer = setTimeout(function () {
          popup.classList.remove('is-visible');
          if (shownCount < maxPopups) {
            showTimer = setTimeout(showPopup, interval);
          }
        }, displayDuration);
      }

      // Close button
      if (closeBtn) {
        closeBtn.addEventListener('click', function () {
          popup.classList.remove('is-visible');
          clearTimeout(hideTimer);
          clearTimeout(showTimer);
          sessionStorage.setItem(dismissKey, 'true');
        });
      }

      // Start after a delay
      showTimer = setTimeout(showPopup, interval / 2);
    });
  }

  /* ===================================================================
     INIT
     =================================================================== */
  function init() {
    initTabs();
    initLoadMore();
    initReadMore();
    initPhotoModal();
    initCarousels();
    initReviewForm();
    initSocialProof();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();